// frontend/src/pages/technician/TechnicianProfile.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Paper,
    Alert,
    CircularProgress,
    TextField,
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Tab,
    Tabs
} from '@mui/material';
import {
    Person,
    Email,
    Phone,
    Edit,
    Save,
    Lock,
    Badge,
    Work,
    Schedule,
    Assignment,
    Build,
    Star,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import { useTechnicianAuth } from '../../contexts/TechnicianAuthContext';
import { technicianApiService } from '../../services/technicianApiService';

const TechnicianProfile = () => {
    const { technician, updateTechnicianInfo } = useTechnicianAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editDialog, setEditDialog] = useState(false);
    const [passwordDialog, setPasswordDialog] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [editForm, setEditForm] = useState({
        name: '',
        phone: '',
        email: ''
    });

    const [passwordForm, setPasswordForm] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const data = await technicianApiService.getProfile();
            setProfile(data.profile);

            // Inicializar formulario de edición
            setEditForm({
                name: data.profile.name || '',
                phone: data.profile.phone || '',
                email: data.profile.email || ''
            });

            setError(null);
        } catch (error) {
            console.error('Error cargando perfil:', error);
            setError('Error cargando información del perfil');
        } finally {
            setLoading(false);
        }
    };

    const handleEditFormChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePasswordFormChange = (field, value) => {
        setPasswordForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const saveProfile = async () => {
        try {
            await technicianApiService.updateProfile(editForm);

            // Actualizar contexto
            updateTechnicianInfo({
                name: editForm.name,
                email: editForm.email
            });

            setEditDialog(false);
            setSuccess('Perfil actualizado correctamente');
            await loadProfile();
        } catch (error) {
            console.error('Error actualizando perfil:', error);
            setError('Error actualizando perfil');
        }
    };

    const changePassword = async () => {
        if (passwordForm.new_password !== passwordForm.confirm_password) {
            setError('Las contraseñas no coinciden');
            return;
        }

        if (passwordForm.new_password.length < 6) {
            setError('La nueva contraseña debe tener al menos 6 caracteres');
            return;
        }

        try {
            await technicianApiService.changePassword({
                current_password: passwordForm.current_password,
                new_password: passwordForm.new_password
            });

            setPasswordForm({
                current_password: '',
                new_password: '',
                confirm_password: ''
            });

            setPasswordDialog(false);
            setSuccess('Contraseña actualizada correctamente');
        } catch (error) {
            console.error('Error cambiando contraseña:', error);
            setError(error.response?.data?.error || 'Error cambiando contraseña');
        }
    };

    const getInitials = (name) => {
        return name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'T';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (!profile) {
        return (
            <Alert severity="error">
                Error cargando información del perfil
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
            {/* Header */}
            <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
                <Avatar
                    sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: 'primary.main',
                        fontSize: '2rem'
                    }}
                >
                    {getInitials(profile.name)}
                </Avatar>
                <Typography variant="h4" gutterBottom>
                    {profile.name}
                </Typography>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                    Técnico de Campo
                </Typography>
                <Chip
                    label={profile.status === 'active' ? 'Activo' : 'Inactivo'}
                    color={profile.status === 'active' ? 'success' : 'default'}
                />
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
                    <Tab label="Información Personal" icon={<Person />} />
                    <Tab label="Estadísticas" icon={<Assignment />} />
                </Tabs>
            </Paper>

            {/* Tab 0: Información Personal */}
            {currentTab === 0 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">
                                        Información Personal
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Edit />}
                                        onClick={() => setEditDialog(true)}
                                    >
                                        Editar
                                    </Button>
                                </Box>

                                <List>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Person />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Nombre Completo"
                                            secondary={profile.name}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Email />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Correo Electrónico"
                                            secondary={profile.email}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Phone />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Teléfono"
                                            secondary={profile.phone || 'No especificado'}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Badge />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Usuario"
                                            secondary={profile.username}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Work />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Rol"
                                            secondary={profile.role_name}
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Seguridad de la Cuenta
                                </Typography>

                                <List>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Lock />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Contraseña"
                                            secondary="••••••••"
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setPasswordDialog(true)}
                                        >
                                            Cambiar
                                        </Button>
                                    </ListItem>
                                </List>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle1" gutterBottom>
                                    Información de Acceso
                                </Typography>

                                <Typography variant="body2" color="textSecondary">
                                    <strong>Último acceso:</strong> {new Date().toLocaleString('es-PE')}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Tab 1: Estadísticas */}
            {currentTab === 1 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Assignment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                <Typography variant="h4" gutterBottom>
                                    0
                                </Typography>
                                <Typography color="textSecondary">
                                    Instalaciones Este Mes
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Build sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                                <Typography variant="h4" gutterBottom>
                                    0
                                </Typography>
                                <Typography color="textSecondary">
                                    Tickets Resueltos
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Schedule sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                                <Typography variant="h4" gutterBottom>
                                    0h
                                </Typography>
                                <Typography color="textSecondary">
                                    Horas Trabajadas
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Star sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                                <Typography variant="h4" gutterBottom>
                                    5.0
                                </Typography>
                                <Typography color="textSecondary">
                                    Calificación Promedio
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Resumen de Actividad
                                </Typography>
                                <Alert severity="info">
                                    Las estadísticas detalladas estarán disponibles después de completar algunas instalaciones y tickets.
                                </Alert>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Dialog para editar perfil */}
            <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Editar Información Personal</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Nombre Completo"
                                value={editForm.name}
                                onChange={(e) => handleEditFormChange('name', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Correo Electrónico"
                                type="email"
                                value={editForm.email}
                                onChange={(e) => handleEditFormChange('email', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Teléfono"
                                value={editForm.phone}
                                onChange={(e) => handleEditFormChange('phone', e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={saveProfile}
                        variant="contained"
                        startIcon={<Save />}
                    >
                        Guardar Cambios
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para cambiar contraseña */}
            <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Cambiar Contraseña</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Contraseña Actual"
                                type={showPasswords.current ? 'text' : 'password'}
                                value={passwordForm.current_password}
                                onChange={(e) => handlePasswordFormChange('current_password', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <Button
                                            onClick={() => togglePasswordVisibility('current')}
                                            sx={{ minWidth: 'auto', p: 1 }}
                                        >
                                            {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                                        </Button>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Nueva Contraseña"
                                type={showPasswords.new ? 'text' : 'password'}
                                value={passwordForm.new_password}
                                onChange={(e) => handlePasswordFormChange('new_password', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <Button
                                            onClick={() => togglePasswordVisibility('new')}
                                            sx={{ minWidth: 'auto', p: 1 }}
                                        >
                                            {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                        </Button>
                                    )
                                }}
                                helperText="Mínimo 6 caracteres"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Confirmar Nueva Contraseña"
                                type={showPasswords.confirm ? 'text' : 'password'}
                                value={passwordForm.confirm_password}
                                onChange={(e) => handlePasswordFormChange('confirm_password', e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <Button
                                            onClick={() => togglePasswordVisibility('confirm')}
                                            sx={{ minWidth: 'auto', p: 1 }}
                                        >
                                            {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                                        </Button>
                                    )
                                }}
                                error={passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password}
                                helperText={
                                    passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password
                                        ? 'Las contraseñas no coinciden'
                                        : ''
                                }
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordDialog(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={changePassword}
                        variant="contained"
                        disabled={
                            !passwordForm.current_password ||
                            !passwordForm.new_password ||
                            passwordForm.new_password !== passwordForm.confirm_password ||
                            passwordForm.new_password.length < 6
                        }
                    >
                        Cambiar Contraseña
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TechnicianProfile;